import { useState, useEffect, useMemo } from 'react';
import { X, Search, Package, Check } from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';

interface Ativo {
  id: string;
  equipamento?: string;
  tag?: string;
  area?: string;
  local?: string;
  tipo?: string;
  numeroAprPraxis?: string;
  numeroAmt?: string;
  refSismanaAntigo?: string;
  refSismanaNovo?: string;
}

interface ModalSelecionarAtivoProps {
  aberto: boolean;
  ativoSelecionadoId?: string;
  onFechar: () => void;
  onSelecionar: (ativo: Ativo) => void;
}

type MetodoBusca = 'equipamento' | 'numeroPraxis' | 'numeroAmt' | 'refSismana';

export function ModalSelecionarAtivo({
  aberto,
  ativoSelecionadoId,
  onFechar,
  onSelecionar,
}: ModalSelecionarAtivoProps) {
  const { projeto } = useProjeto();
  const { t } = useI18n();
  const [metodoBusca, setMetodoBusca] = useState<MetodoBusca>('equipamento');
  const [termoBusca, setTermoBusca] = useState('');
  const [todosAtivos, setTodosAtivos] = useState<Ativo[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [mostrarMais, setMostrarMais] = useState(false);
  const limiteExibicao = 10;

  useEffect(() => {
    if (aberto) {
      setTermoBusca('');
      setMostrarMais(false);
      carregarTodosAtivos();
    }
  }, [aberto, projeto?.id]);

  const carregarTodosAtivos = async () => {
    if (!projeto?.id) {
      setTodosAtivos([]);
      return;
    }
    setCarregando(true);
    try {
      const response = await apiService.listarAtivos({ projetoId: projeto.id, limit: 10000 });
      if (response.success && response.data) {
        const ativosLista = Array.isArray(response.data) ? response.data : [];
        setTodosAtivos(ativosLista);
      }
    } catch (error) {
      console.error('Erro ao carregar ativos:', error);
    } finally {
      setCarregando(false);
    }
  };

  // Filtrar ativos localmente baseado no método de busca e termo
  const ativosFiltrados = useMemo(() => {
    if (!termoBusca.trim()) {
      return todosAtivos;
    }

    const termoLower = termoBusca.toLowerCase();
    return todosAtivos.filter((ativo) => {
      switch (metodoBusca) {
        case 'numeroPraxis':
          return ativo.numeroAprPraxis?.toLowerCase().includes(termoLower);
        case 'numeroAmt':
          return ativo.numeroAmt?.toLowerCase().includes(termoLower);
        case 'refSismana':
          return (
            ativo.refSismanaAntigo?.toLowerCase().includes(termoLower) ||
            ativo.refSismanaNovo?.toLowerCase().includes(termoLower)
          );
        case 'equipamento':
        default:
          return (
            ativo.equipamento?.toLowerCase().includes(termoLower) ||
            ativo.tag?.toLowerCase().includes(termoLower)
          );
      }
    });
  }, [todosAtivos, termoBusca, metodoBusca]);

  // Limitar exibição aos primeiros N resultados
  const ativosExibidos = mostrarMais 
    ? ativosFiltrados 
    : ativosFiltrados.slice(0, limiteExibicao);
  
  const temMaisResultados = ativosFiltrados.length > limiteExibicao;

  const handleSelecionar = (ativo: Ativo) => {
    onSelecionar(ativo);
    onFechar();
  };

  const getLabelMetodo = (metodo: MetodoBusca): string => {
    switch (metodo) {
      case 'equipamento':
      case 'numeroPraxis':
      case 'numeroAmt':
      case 'refSismana':
        return t(`apreciacaoModais.selecionarAtivo.metodo.${metodo}`);
      default:
        return '';
    }
  };

  const getPlaceholder = (): string => {
    switch (metodoBusca) {
      case 'equipamento':
      case 'numeroPraxis':
      case 'numeroAmt':
      case 'refSismana':
        return t(`apreciacaoModais.selecionarAtivo.placeholder.${metodoBusca}`);
      default:
        return t('apreciacaoModais.selecionarAtivo.placeholder.padrao');
    }
  };

  const getValorExibicao = (ativo: Ativo): string => {
    switch (metodoBusca) {
      case 'numeroPraxis':
        return ativo.numeroAprPraxis || '-';
      case 'numeroAmt':
        return ativo.numeroAmt || '-';
      case 'refSismana':
        return ativo.refSismanaNovo || ativo.refSismanaAntigo || '-';
      case 'equipamento':
      default:
        return ativo.equipamento || t('apreciacaoModais.selecionarAtivo.semNome');
    }
  };

  if (!aberto) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onFechar}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-schmersal-gray-darker rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-schmersal-blue px-6 py-4 flex items-center justify-between">
            <div className="flex items-center">
              <Package className="h-6 w-6 text-white mr-2" />
              <h3 className="text-lg font-bold text-white">{t('apreciacaoModais.selecionarAtivo.titulo')}</h3>
            </div>
            <button
              onClick={onFechar}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="px-6 py-4">
            {/* Métodos de Busca */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('apreciacaoModais.selecionarAtivo.metodoRotulo')}
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {(['equipamento', 'numeroPraxis', 'numeroAmt', 'refSismana'] as MetodoBusca[]).map((metodo) => (
                <button
                  key={metodo}
                  type="button"
                  onClick={() => {
                    setMetodoBusca(metodo);
                    setTermoBusca('');
                    setMostrarMais(false);
                  }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
                      metodoBusca === metodo
                        ? 'border-schmersal-blue bg-schmersal-blue text-white'
                        : 'border-schmersal-gray-light dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white hover:border-schmersal-blue'
                    }`}
                  >
                    {getLabelMetodo(metodo)}
                  </button>
                ))}
              </div>
            </div>

            {/* Campo de Busca */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('apreciacaoModais.selecionarAtivo.buscarPor', { metodo: getLabelMetodo(metodoBusca) })}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-schmersal-gray-medium dark:text-schmersal-gray-light" />
                <input
                  type="text"
                  value={termoBusca}
                  onChange={(e) => {
                    setTermoBusca(e.target.value);
                    setMostrarMais(false);
                  }}
                  placeholder={getPlaceholder()}
                  className="pl-10 block w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg 
                           shadow-sm focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white
                           placeholder-schmersal-gray-medium dark:placeholder-schmersal-gray-medium
                           sm:text-sm py-2.5 transition-all duration-200"
                  autoFocus
                />
              </div>
            </div>

            {/* Lista de Resultados */}
            <div className="border border-rg-line dark:border-schmersal-gray-dark rounded-lg max-h-96 overflow-y-auto">
              {carregando ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-schmersal-blue"></div>
                  <p className="mt-3 text-sm text-schmersal-gray-medium dark:text-white font-medium">{t('apreciacaoModais.selecionarAtivo.carregando')}</p>
                </div>
              ) : ativosFiltrados.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="mx-auto h-16 w-16 text-schmersal-gray-medium dark:text-schmersal-gray-light" />
                  <p className="mt-3 text-sm text-schmersal-gray-medium dark:text-white font-medium">
                    {termoBusca.trim()
                      ? t('apreciacaoModais.selecionarAtivo.vazioBusca')
                      : t('apreciacaoModais.selecionarAtivo.vazioLista')}
                  </p>
                </div>
              ) : (
                <>
                  <div className="divide-y divide-rg-hairline dark:divide-schmersal-gray-dark">
                    {ativosExibidos.map((ativo) => (
                      <button
                        key={ativo.id}
                        type="button"
                        onClick={() => handleSelecionar(ativo)}
                        className={`w-full text-left px-4 py-3 hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors ${
                          ativoSelecionadoId === ativo.id
                            ? 'bg-schmersal-blue bg-opacity-10 dark:bg-schmersal-blue dark:bg-opacity-20'
                            : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              {ativoSelecionadoId === ativo.id && (
                                <Check className="h-5 w-5 text-schmersal-blue mr-2" />
                              )}
                              <span className="text-sm font-semibold text-schmersal-gray-dark dark:text-white">
                                {getValorExibicao(ativo)}
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                              {ativo.equipamento && metodoBusca !== 'equipamento' && (
                                <span>{t('apreciacaoModais.selecionarAtivo.equipamento', { equipamento: ativo.equipamento })}</span>
                              )}
                              {ativo.tag && (
                                <span className={ativo.equipamento && metodoBusca !== 'equipamento' ? ' • ' : ''}>
                                  {t('apreciacaoModais.selecionarAtivo.tag', { tag: ativo.tag })}
                                </span>
                              )}
                              {ativo.area && (
                                <span className=" • ">{ativo.area}</span>
                              )}
                              {ativo.local && (
                                <span> / {ativo.local}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {/* Mostrar Mais Resultados */}
                  {temMaisResultados && !mostrarMais && (
                    <div className="px-4 py-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark text-center">
                      <button
                        type="button"
                        onClick={() => setMostrarMais(true)}
                        className="px-4 py-2 text-sm font-medium text-schmersal-blue dark:text-white 
                                 border border-schmersal-blue rounded-lg hover:bg-schmersal-blue hover:text-white 
                                 dark:hover:bg-schmersal-blue transition-all duration-200"
                      >
                        {t('apreciacaoModais.lista.mostrarMais', { restantes: ativosFiltrados.length - limiteExibicao })}
                      </button>
                    </div>
                  )}
                  
                  {mostrarMais && temMaisResultados && (
                    <div className="px-4 py-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark text-center">
                      <button
                        type="button"
                        onClick={() => setMostrarMais(false)}
                        className="px-4 py-2 text-sm font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light 
                                 border border-rg-line dark:border-schmersal-gray-dark rounded-lg 
                                 hover:bg-schmersal-gray-medium hover:text-white transition-all duration-200"
                      >
                        {t('apreciacaoModais.lista.mostrarMenos')}
                      </button>
                    </div>
                  )}
                  
                  {/* Contador de resultados */}
                  {termoBusca.trim() && (
                    <div className="px-4 py-2 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark bg-schmersal-gray-light dark:bg-schmersal-gray-dark">
                      <p className="text-xs text-center text-schmersal-gray-medium dark:text-schmersal-gray-light">
                        {ativosFiltrados.length === 1
                          ? t('apreciacaoModais.lista.resultadoUm', { total: ativosFiltrados.length })
                          : t('apreciacaoModais.lista.resultadoVarios', { total: ativosFiltrados.length })}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-schmersal-gray-light dark:bg-schmersal-gray-dark px-6 py-3 flex justify-end">
            <button
              type="button"
              onClick={onFechar}
              className="px-4 py-2 text-sm font-medium text-schmersal-gray-dark dark:text-white 
                       border border-rg-line dark:border-schmersal-gray-dark rounded-lg 
                       hover:bg-schmersal-gray-medium hover:text-white transition-all duration-200"
            >
              {t('comum.acao.cancelar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

